// Global function to open chat (called from message artist button) - Available immediately
window.openSendbirdChat = function(artistData) {
  console.log('openSendbirdChat called with:', artistData);
  
  if (!window.sendbirdMessages) {
    console.error('Sendbird not initialized yet. Please wait and try again.');
    alert('Chat is loading. Please wait a moment and try again.');
    return;
  }
  
  if (!window.sendbirdMessages.isInitialized) {
    console.error('Sendbird not initialized');
    alert('Chat is not available. Please check your login status and try again.');
    return;
  }
  
  try {
    window.sendbirdMessages.openChatModal(artistData);
  } catch (error) {
    console.error('Error opening chat:', error);
    alert('Failed to open chat. Please try again.');
  }
};

// Sendbird Messages Integration using REST API
class SendbirdMessages {
  constructor() {
    this.appId = window.themeVariables?.settings?.sendbirdAppId || '';
    this.apiToken = window.themeVariables?.settings?.sendbirdApiToken || '';
    this.baseUrl = `https://api-${this.appId}.sendbird.com/v3`;
    this.currentUser = null;
    this.currentChannel = null;
    this.isInitialized = false;
    this.channels = [];
    this.selectedChannelUrl = null;
    this.messages = new Map(); // Store messages by channel URL
  }

  // Initialize Sendbird with current customer data
  async initialize() {
    if (!this.appId || !this.apiToken) {
      console.error('Sendbird App ID or API Token not configured');
      return false;
    }

    // Get current customer from Shopify
    const customer = window.FreshMarketerSourceMap?.currentCustomer;

    // const customer = {
    //   id: 1,
    //   name:  'Test Test',
    //   email: 'test@test.com'
    // };
    if (!customer) {
      console.error('No customer logged in');
      this.showLoginRequired();
      return false;
    }

    this.currentUser = {
      userId: `shopify_${customer.id}`,
      nickname: customer.name || customer.email,
      profileUrl: '', // You can add profile image URL here if available
      metadata: {
        shopify_customer_id: customer.id,
        email: customer.email,
        total_orders: customer.total_orders,
        total_spent: customer.total_spent
      }
    };

    try {
      // Create or update user in Sendbird
      await this.createOrUpdateUser(this.currentUser);
      this.isInitialized = true;
      
      // Load channels and check for artist parameter
      await this.loadChannels();
      this.checkArtistParameter();
      
      return true;
    } catch (error) {
      console.error('Failed to initialize Sendbird:', error);
      return false;
    }
  }

  // Check if there's an artist parameter in URL
  checkArtistParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const artistId = urlParams.get('artist');
    
    if (artistId) {
      // Find or create channel with this artist
      this.openChannelWithArtist(artistId);
    }
  }

  // Open channel with specific artist
  async openChannelWithArtist(artistId) {
    try {
      // First, we need to get artist data - this would typically come from your backend
      // For now, we'll create a basic artist user
      const artistUser = {
        userId: `artist_${artistId}`,
        nickname: `Artist ${artistId}`,
        profileUrl: '',
        metadata: {
          artist_id: artistId,
          type: 'artist'
        }
      };

      // Create or update artist user
      await this.createOrUpdateUser(artistUser);

      // Create or get channel
      const channel = await this.createOrGetChannel(artistUser.userId);
      
      // Select this channel
      this.selectChannel(channel.channel_url);
      
    } catch (error) {
      console.error('Error opening channel with artist:', error);
      this.showError('Failed to start conversation with artist');
    }
  }

  // Open chat modal with messages page UI
  openChatModal(artistData) {
    if (!this.isInitialized) {
      console.error('Sendbird not initialized');
      return;
    }

    // Create and show modal with messages page UI
    this.showMessagesModal(artistData);
  }

  // Show login required message
  showLoginRequired() {
    const chatContent = document.getElementById('chat-content');
    if (chatContent) {
      chatContent.innerHTML = `
        <div class="no-channel-selected">
          <div class="no-channel-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="m22 21-3-3m0 0a2 2 0 1 0-4 0 2 2 0 0 0 4 0Z"/>
            </svg>
          </div>
          <h3>Login Required</h3>
          <p>Please log in to your account to start messaging artists</p>
          <a href="/account/login" class="btn btn-layout1" style="margin-top: 16px;">Login</a>
        </div>
      `;
    }
  }

  // Create or update user in Sendbird
  async createOrUpdateUser(user) {
    const url = `${this.baseUrl}/users/${user.userId}`;
    
    try {
      // Try to get user first
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (response.ok) {
        // User exists, update if needed
        const existingUser = await response.json();
        if (this.shouldUpdateUser(existingUser, user)) {
          return await this.updateUser(user);
        }
        return existingUser;
      } else {
        // User doesn't exist, create new user
        return await this.createUser(user);
      }
    } catch (error) {
      console.error('Error checking user existence:', error);
      // If check fails, try to create user
      return await this.createUser(user);
    }
  }

  // Create new user
  async createUser(user) {
    const url = `${this.baseUrl}/users`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        user_id: user.userId,
        nickname: user.nickname,
        profile_url: user.profileUrl,
        metadata: user.metadata
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create user');
    }

    return await response.json();
  }

  // Update existing user
  async updateUser(user) {
    const url = `${this.baseUrl}/users/${user.userId}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({
        nickname: user.nickname,
        profile_url: user.profileUrl,
        metadata: user.metadata
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update user');
    }

    return await response.json();
  }

  // Check if user should be updated
  shouldUpdateUser(existingUser, newUser) {
    return (
      existingUser.nickname !== newUser.nickname ||
      existingUser.profile_url !== newUser.profileUrl ||
      JSON.stringify(existingUser.metadata) !== JSON.stringify(newUser.metadata)
    );
  }

  // Load all channels for current user
  async loadChannels() {
    if (!this.isInitialized) return;

    try {
      const url = `${this.baseUrl}/users/${this.currentUser.userId}/group_channels?limit=50`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load channels');
      }

      const data = await response.json();
      this.channels = data.channels || [];
      this.renderChannelsList();
    } catch (error) {
      console.error('Error loading channels:', error);
      this.showError('Failed to load conversations');
    }
  }

  // Render channels list
  renderChannelsList() {
    const channelsList = document.getElementById('channels-list');
    if (!channelsList) return;

    if (this.channels.length === 0) {
      channelsList.innerHTML = `
        <div class="channels-loading">
          <p>No conversations yet</p>
          <p style="font-size: 14px; color: #9ca3af; margin-top: 8px;">Start a conversation with an artist to see it here</p>
        </div>
      `;
      return;
    }

    const channelsHTML = this.channels.map(channel => {
      const otherMember = channel.members.find(member => member.user_id !== this.currentUser.userId);
      const lastMessage = channel.last_message;
      const unreadCount = channel.unread_message_count || 0;
      
      return `
        <div class="channel-item ${channel.channel_url === this.selectedChannelUrl ? 'active' : ''}" 
             onclick="sendbirdMessages.selectChannel('${channel.channel_url}')">
          <div class="channel-avatar">
            ${otherMember ? otherMember.nickname.charAt(0).toUpperCase() : 'A'}
          </div>
          <div class="channel-info">
            <div class="channel-name">${otherMember ? otherMember.nickname : 'Unknown User'}</div>
            <div class="channel-last-message">
              ${lastMessage ? this.formatLastMessage(lastMessage) : 'No messages yet'}
            </div>
          </div>
          <div class="channel-meta">
            <div class="channel-time">${this.formatTime(channel.last_message_at)}</div>
            ${unreadCount > 0 ? `<div class="channel-unread">${unreadCount}</div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    channelsList.innerHTML = channelsHTML;
  }

  // Format last message for display
  formatLastMessage(message) {
    if (message.message_type === 'MESG') {
      return message.message || 'Sent a message';
    } else if (message.message_type === 'FILE') {
      return 'Sent a file';
    } else if (message.message_type === 'ADMM') {
      return 'Admin message';
    }
    return 'New message';
  }

  // Format timestamp
  formatTime(timestamp) {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  // Create or get existing channel with artist
  async createOrGetChannel(artistUserId) {
    if (!this.isInitialized) {
      throw new Error('Sendbird not initialized');
    }

    try {
      // Check if channel already exists
      const existingChannel = await this.getExistingChannel([this.currentUser.userId, artistUserId]);
      
      if (existingChannel) {
        this.currentChannel = existingChannel;
        return existingChannel;
      }

      // Create new channel
      const newChannel = await this.createChannel([this.currentUser.userId, artistUserId]);
      this.currentChannel = newChannel;
      return newChannel;
    } catch (error) {
      console.error('Error creating/getting channel:', error);
      throw error;
    }
  }

  // Get existing channel
  async getExistingChannel(userIds) {
    const url = `${this.baseUrl}/group_channels?members_exactly_in=${userIds.join(',')}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.channels && data.channels.length > 0 ? data.channels[0] : null;
  }

  // Create new channel
  async createChannel(userIds) {
    const url = `${this.baseUrl}/group_channels`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        user_ids: userIds,
        name: `Chat with Artist`
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create channel');
    }

    return await response.json();
  }

  // Select a channel
  async selectChannel(channelUrl) {
    this.selectedChannelUrl = channelUrl;
    
    // Update UI
    this.updateChannelSelection();
    this.showChannelHeader(channelUrl);
    this.showChatInput();
    
    // Load messages for this channel
    await this.loadMessages(channelUrl);
  }

  // Update channel selection in sidebar
  updateChannelSelection() {
    const channelItems = document.querySelectorAll('.channel-item');
    channelItems.forEach(item => {
      item.classList.remove('active');
    });
    
    const activeItem = document.querySelector(`[onclick*="${this.selectedChannelUrl}"]`);
    if (activeItem) {
      activeItem.classList.add('active');
    }
  }

  // Show channel header
  showChannelHeader(channelUrl) {
    const channel = this.channels.find(c => c.channel_url === channelUrl);
    if (!channel) return;

    const otherMember = channel.members.find(member => member.user_id !== this.currentUser.userId);
    const chatHeader = document.getElementById('chat-header');
    const chatUserName = document.getElementById('chat-user-name');
    const chatAvatar = document.getElementById('chat-avatar');

    if (chatHeader) {
      chatHeader.style.display = 'flex';
    }

    if (chatUserName) {
      chatUserName.textContent = otherMember ? otherMember.nickname : 'Unknown User';
    }

    if (chatAvatar) {
      chatAvatar.textContent = otherMember ? otherMember.nickname.charAt(0).toUpperCase() : 'A';
    }
  }

  // Show chat input
  showChatInput() {
    const chatInputArea = document.getElementById('chat-input-area');
    if (chatInputArea) {
      chatInputArea.style.display = 'block';
    }
  }

  // Load messages for a channel
  async loadMessages(channelUrl) {
    try {
      const url = `${this.baseUrl}/group_channels/${channelUrl}/messages?limit=50&reverse=true`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();
      const messages = data.messages || [];
      this.messages.set(channelUrl, messages);
      this.renderMessages(messages);
    } catch (error) {
      console.error('Error loading messages:', error);
      this.showError('Failed to load messages');
    }
  }

  // Render messages
  renderMessages(messages) {
    const chatContent = document.getElementById('chat-content');
    if (!chatContent) return;

    if (messages.length === 0) {
      chatContent.innerHTML = `
        <div class="no-channel-selected">
          <div class="no-channel-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h3>Start the conversation</h3>
          <p>Send your first message to begin chatting</p>
        </div>
      `;
      return;
    }

    const messagesHTML = messages.map(message => {
      const isOwn = message.user.user_id === this.currentUser.userId;
      const messageTime = new Date(message.created_at).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      return `
        <div class="message-item ${isOwn ? 'own' : 'other'}">
          <div class="message-avatar">
            ${message.user.nickname.charAt(0).toUpperCase()}
          </div>
          <div class="message-content">
            <div class="message-bubble">
              ${message.message || 'Sent a message'}
            </div>
            <div class="message-time">${messageTime}</div>
          </div>
        </div>
      `;
    }).join('');

    chatContent.innerHTML = messagesHTML;
    this.scrollToBottom();
  }

  // Scroll to bottom of messages
  scrollToBottom() {
    const chatContent = document.getElementById('chat-content');
    if (chatContent) {
      chatContent.scrollTop = chatContent.scrollHeight;
    }
  }

  // Send message
  async sendMessage(message, channelUrl) {
    const url = `${this.baseUrl}/group_channels/${channelUrl}/messages`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        message_type: 'MESG',
        message: message,
        user_id: this.currentUser.userId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send message');
    }

    return await response.json();
  }

  // Send message from input
  async sendMessageFromInput() {
    const messageInput = document.getElementById('chat-message-input');
    const message = messageInput.value.trim();

    if (!message || !this.selectedChannelUrl) return;

    try {
      await this.sendMessage(message, this.selectedChannelUrl);
      messageInput.value = '';
      
      // Reload messages to show the new one
      await this.loadMessages(this.selectedChannelUrl);
      
      // Reload channels to update last message
      await this.loadChannels();
    } catch (error) {
      console.error('Error sending message:', error);
      this.showError('Failed to send message. Please try again.');
    }
  }

  // Show channel list (for mobile)
  showChannelList() {
    const sidebar = document.querySelector('.messages-sidebar');
    if (sidebar) {
      sidebar.classList.add('show');
    }
  }

  // Hide channel list (for mobile)
  hideChannelList() {
    const sidebar = document.querySelector('.messages-sidebar');
    if (sidebar) {
      sidebar.classList.remove('show');
    }
  }

  // Show error message
  showError(message) {
    const chatContent = document.getElementById('chat-content');
    if (chatContent) {
      chatContent.innerHTML = `
        <div class="no-channel-selected">
          <div class="no-channel-icon" style="color: #dc2626;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>Error</h3>
          <p>${message}</p>
        </div>
      `;
    }
  }

  // Show messages modal with full-page UI
  showMessagesModal(artistData) {
    // Remove existing modal if any
    const existingModal = document.getElementById('sendbird-messages-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal HTML with messages page UI
    const modalHTML = `
      <div id="sendbird-messages-modal" class="sendbird-messages-modal">
        <div class="sendbird-modal-overlay"></div>
        <div class="sendbird-modal-content">
          <div class="messages-container">
            <!-- Channel List Sidebar -->
            <div class="messages-sidebar">
              <div class="messages-header">
                <h2>Messages</h2>
                <div class="messages-search">
                  <input type="text" id="modal-messages-search-input" placeholder="Search" />
                  <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </div>
              </div>
              
              <div class="channels-list" id="modal-channels-list">
                <div class="channels-loading">Loading conversations...</div>
              </div>
            </div>

            <!-- Main Chat Area -->
            <div class="messages-main">
              <div class="chat-header" id="modal-chat-header" style="display: none;">
                <div class="chat-user-info">
                  <div class="chat-avatar" id="modal-chat-avatar"></div>
                  <div class="chat-user-details">
                    <h3 id="modal-chat-user-name"></h3>
                    <span id="modal-chat-user-status">Online</span>
                  </div>
                </div>
                <button class="chat-back-btn" id="modal-chat-back-btn" onclick="sendbirdMessages.showChannelList()">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="m15 18-6-6 6-6"/>
                  </svg>
                </button>
                <button class="sendbird-close-btn" onclick="sendbirdMessages.closeMessagesModal()">&times;</button>
              </div>

              <div class="chat-content" id="modal-chat-content">
                <div class="no-channel-selected">
                  <div class="no-channel-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <h3>No conversation selected</h3>
                  <p>Choose a conversation from the sidebar to start messaging</p>
                </div>
              </div>

              <div class="chat-input-area" id="modal-chat-input-area" style="display: none;">
                <div class="chat-input-container">
                  <button class="chat-attachment-btn" id="modal-chat-attachment-btn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"/>
                    </svg>
                  </button>
                  <input type="text" id="modal-chat-message-input" placeholder="Type a message" />
                  <button class="chat-send-btn" id="modal-chat-send-btn">Send</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Initialize chat with artist
    this.initializeModalChat(artistData);

    // Add event listeners for modal
    this.addModalEventListeners();
  }

  // Initialize modal chat with artist
  async initializeModalChat(artistData) {
    try {
      // Create artist user in Sendbird
      const artistUser = {
        userId: `artist_${artistData.id}`,
        nickname: artistData.name,
        profileUrl: artistData.profile_image || '',
        metadata: {
          artist_id: artistData.id,
          type: 'artist'
        }
      };

      await this.createOrUpdateUser(artistUser);

      // Create or get channel
      const channel = await this.createOrGetChannel(artistUser.userId);
      
      // Load channels for sidebar
      await this.loadModalChannels();
      
      // Select this channel
      this.selectModalChannel(channel.channel_url);
      
    } catch (error) {
      console.error('Error initializing modal chat:', error);
      this.showModalError('Failed to initialize chat. Please try again.');
    }
  }

  // Load channels for modal sidebar
  async loadModalChannels() {
    if (!this.isInitialized) return;

    try {
      const url = `${this.baseUrl}/users/${this.currentUser.userId}/group_channels?limit=50`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load channels');
      }

      const data = await response.json();
      this.channels = data.channels || [];
      this.renderModalChannelsList();
    } catch (error) {
      console.error('Error loading modal channels:', error);
      this.showModalError('Failed to load conversations');
    }
  }

  // Render channels list in modal
  renderModalChannelsList() {
    const channelsList = document.getElementById('modal-channels-list');
    if (!channelsList) return;

    if (this.channels.length === 0) {
      channelsList.innerHTML = `
        <div class="channels-loading">
          <p>No conversations yet</p>
          <p style="font-size: 14px; color: #9ca3af; margin-top: 8px;">Start a conversation with an artist to see it here</p>
        </div>
      `;
      return;
    }

    const channelsHTML = this.channels.map(channel => {
      const otherMember = channel.members.find(member => member.user_id !== this.currentUser.userId);
      const lastMessage = channel.last_message;
      const unreadCount = channel.unread_message_count || 0;
      
      return `
        <div class="channel-item ${channel.channel_url === this.selectedChannelUrl ? 'active' : ''}" 
             onclick="sendbirdMessages.selectModalChannel('${channel.channel_url}')">
          <div class="channel-avatar">
            ${otherMember ? otherMember.nickname.charAt(0).toUpperCase() : 'A'}
          </div>
          <div class="channel-info">
            <div class="channel-name">${otherMember ? otherMember.nickname : 'Unknown User'}</div>
            <div class="channel-last-message">
              ${lastMessage ? this.formatLastMessage(lastMessage) : 'No messages yet'}
            </div>
          </div>
          <div class="channel-meta">
            <div class="channel-time">${this.formatTime(channel.last_message_at)}</div>
            ${unreadCount > 0 ? `<div class="channel-unread">${unreadCount}</div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    channelsList.innerHTML = channelsHTML;
  }

  // Select channel in modal
  async selectModalChannel(channelUrl) {
    this.selectedChannelUrl = channelUrl;
    
    // Update UI
    this.updateModalChannelSelection();
    this.showModalChannelHeader(channelUrl);
    this.showModalChatInput();
    
    // Load messages for this channel
    await this.loadModalMessages(channelUrl);
  }

  // Update channel selection in modal sidebar
  updateModalChannelSelection() {
    const channelItems = document.querySelectorAll('#modal-channels-list .channel-item');
    channelItems.forEach(item => {
      item.classList.remove('active');
    });
    
    const activeItem = document.querySelector(`#modal-channels-list [onclick*="${this.selectedChannelUrl}"]`);
    if (activeItem) {
      activeItem.classList.add('active');
    }
  }

  // Show channel header in modal
  showModalChannelHeader(channelUrl) {
    const channel = this.channels.find(c => c.channel_url === channelUrl);
    if (!channel) return;

    const otherMember = channel.members.find(member => member.user_id !== this.currentUser.userId);
    const chatHeader = document.getElementById('modal-chat-header');
    const chatUserName = document.getElementById('modal-chat-user-name');
    const chatAvatar = document.getElementById('modal-chat-avatar');

    if (chatHeader) {
      chatHeader.style.display = 'flex';
    }

    if (chatUserName) {
      chatUserName.textContent = otherMember ? otherMember.nickname : 'Unknown User';
    }

    if (chatAvatar) {
      chatAvatar.textContent = otherMember ? otherMember.nickname.charAt(0).toUpperCase() : 'A';
    }
  }

  // Show chat input in modal
  showModalChatInput() {
    const chatInputArea = document.getElementById('modal-chat-input-area');
    if (chatInputArea) {
      chatInputArea.style.display = 'block';
    }
  }

  // Load messages for modal channel
  async loadModalMessages(channelUrl) {
    try {
      const url = `${this.baseUrl}/group_channels/${channelUrl}/messages?limit=50&reverse=true`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();
      const messages = data.messages || [];
      this.messages.set(channelUrl, messages);
      this.renderModalMessages(messages);
    } catch (error) {
      console.error('Error loading modal messages:', error);
      this.showModalError('Failed to load messages');
    }
  }

  // Render messages in modal
  renderModalMessages(messages) {
    const chatContent = document.getElementById('modal-chat-content');
    if (!chatContent) return;

    if (messages.length === 0) {
      chatContent.innerHTML = `
        <div class="no-channel-selected">
          <div class="no-channel-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h3>Start the conversation</h3>
          <p>Send your first message to begin chatting</p>
        </div>
      `;
      return;
    }

    const messagesHTML = messages.map(message => {
      const isOwn = message.user.user_id === this.currentUser.userId;
      const messageTime = new Date(message.created_at).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      return `
        <div class="message-item ${isOwn ? 'own' : 'other'}">
          <div class="message-avatar">
            ${message.user.nickname.charAt(0).toUpperCase()}
          </div>
          <div class="message-content">
            <div class="message-bubble">
              ${message.message || 'Sent a message'}
            </div>
            <div class="message-time">${messageTime}</div>
          </div>
        </div>
      `;
    }).join('');

    chatContent.innerHTML = messagesHTML;
    this.scrollModalToBottom();
  }

  // Scroll to bottom of modal messages
  scrollModalToBottom() {
    const chatContent = document.getElementById('modal-chat-content');
    if (chatContent) {
      chatContent.scrollTop = chatContent.scrollHeight;
    }
  }

  // Send message from modal input
  async sendModalMessageFromInput() {
    const messageInput = document.getElementById('modal-chat-message-input');
    const message = messageInput.value.trim();

    if (!message || !this.selectedChannelUrl) return;

    try {
      await this.sendMessage(message, this.selectedChannelUrl);
      messageInput.value = '';
      
      // Reload messages to show the new one
      await this.loadModalMessages(this.selectedChannelUrl);
      
      // Reload channels to update last message
      await this.loadModalChannels();
    } catch (error) {
      console.error('Error sending modal message:', error);
      this.showModalError('Failed to send message. Please try again.');
    }
  }

  // Show error in modal
  showModalError(message) {
    const chatContent = document.getElementById('modal-chat-content');
    if (chatContent) {
      chatContent.innerHTML = `
        <div class="no-channel-selected">
          <div class="no-channel-icon" style="color: #dc2626;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>Error</h3>
          <p>${message}</p>
        </div>
      `;
    }
  }

  // Add event listeners for modal
  addModalEventListeners() {
    const sendBtn = document.getElementById('modal-chat-send-btn');
    const messageInput = document.getElementById('modal-chat-message-input');

    if (sendBtn && messageInput) {
      sendBtn.addEventListener('click', () => this.sendModalMessageFromInput());
      messageInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.sendModalMessageFromInput();
        }
      });
    }

    // Mobile responsive handling
    const channelItems = document.querySelectorAll('#modal-channels-list .channel-item');
    channelItems.forEach(item => {
      item.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          this.hideModalChannelList();
        }
      });
    });
  }

  // Show channel list in modal (for mobile)
  showModalChannelList() {
    const sidebar = document.querySelector('#sendbird-messages-modal .messages-sidebar');
    if (sidebar) {
      sidebar.classList.add('show');
    }
  }

  // Hide channel list in modal (for mobile)
  hideModalChannelList() {
    const sidebar = document.querySelector('#sendbird-messages-modal .messages-sidebar');
    if (sidebar) {
      sidebar.classList.remove('show');
    }
  }

  // Close messages modal
  closeMessagesModal() {
    const modal = document.getElementById('sendbird-messages-modal');
    if (modal) {
      modal.remove();
    }
  }

  // Get headers for API requests
  getHeaders() {
    return {
      'Api-Token': this.apiToken,
      'Content-Type': 'application/json'
    };
  }
}

// Initialize Sendbird when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  console.log('DOM loaded, initializing Sendbird...');
  
  if (window.themeVariables?.settings?.sendbirdEnabled) {
    try {
      window.sendbirdMessages = new SendbirdMessages();
      const initialized = await window.sendbirdMessages.initialize();
      
      if (initialized) {
        console.log('Sendbird initialized successfully');
        
        // Add event listeners for full-page messages (if on messages page)
        const sendBtn = document.getElementById('chat-send-btn');
        const messageInput = document.getElementById('chat-message-input');

        if (sendBtn && messageInput) {
          sendBtn.addEventListener('click', () => window.sendbirdMessages.sendMessageFromInput());
          messageInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              window.sendbirdMessages.sendMessageFromInput();
            }
          });
        }

        // Mobile responsive handling
        const channelItems = document.querySelectorAll('.channel-item');
        channelItems.forEach(item => {
          item.addEventListener('click', () => {
            if (window.innerWidth <= 768) {
              window.sendbirdMessages.hideChannelList();
            }
          });
        });
      } else {
        console.error('Failed to initialize Sendbird');
      }
    } catch (error) {
      console.error('Error initializing Sendbird:', error);
    }
  } else {
    console.log('Sendbird is disabled in theme settings');
  }
});